import React, { useMemo, useState } from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { getCountryCallingCode } from "libphonenumber-js/max";
import { getPhoneCountries, joinPhone, maxNationalLength, splitPhone } from "../../utils/phone";

/**
 * Phone input with a dial-code picker. `value` and `onChange` use E.164
 * ("+48512345678"); the number part accepts digits only, capped at the
 * longest valid number for the chosen country.
 */
const PhoneField = ({ value, onChange, error, helperText, name = "phone", sx }) => {
    const intl = useIntl();
    const locale = useSelector((state) => state.language?.locale || "pl");
    const countries = useMemo(() => getPhoneCountries(locale), [locale]);
    // The chosen country is state: a half-typed number can't be parsed back
    // into a country, and some countries share a dial code (+1, +7...).
    const [country, setCountry] = useState(() => splitPhone(value).country);
    const selected = countries.find((c) => c.code === country) || countries[0];
    const dial = getCountryCallingCode(selected.code);
    const digits = String(value || "").replace(/\D/g, "");
    const national = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
    const maxLength = maxNationalLength(selected.code);

    const change = (nextCountry, nextNational) => {
        const clean = String(nextNational).replace(/\D/g, "").slice(0, maxNationalLength(nextCountry));
        setCountry(nextCountry);
        onChange(joinPhone(nextCountry, clean));
    };

    return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", ...sx }}>
            <Autocomplete
                options={countries}
                value={selected}
                disableClearable
                autoHighlight
                onChange={(_, option) => option && change(option.code, national)}
                getOptionLabel={(o) => `${o.flag} +${o.dial}`}
                isOptionEqualToValue={(o, v) => o.code === v.code}
                filterOptions={(options, { inputValue }) => {
                    const q = inputValue.trim().toLowerCase().replace(/^\+/, "");
                    if (!q || q === `${selected.flag} ${selected.dial}`.toLowerCase()) return options;
                    return options.filter(
                        (o) => o.name.toLowerCase().includes(q) || o.dial.startsWith(q) || o.code.toLowerCase() === q
                    );
                }}
                renderOption={(props, o) => {
                    const { key, ...rest } = props;
                    return (
                        <li key={o.code} {...rest}>
                            <span style={{ marginRight: 8 }}>{o.flag}</span>
                            <span style={{ flex: 1 }}>{o.name}</span>
                            <span style={{ opacity: 0.6, marginLeft: 8 }}>+{o.dial}</span>
                        </li>
                    );
                }}
                slotProps={{ popper: { style: { width: 280 }, placement: "bottom-start" } }}
                sx={{ width: 130, flexShrink: 0 }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={intl.formatMessage({ id: "phone_country" })}
                        error={!!error}
                    />
                )}
            />
            <TextField
                required
                fullWidth
                name={name}
                type="tel"
                label={intl.formatMessage({ id: "phone" })}
                value={national}
                onChange={(e) => change(selected.code, e.target.value)}
                error={!!error}
                helperText={helperText}
                autoComplete="tel-national"
                placeholder={"123456789012345".slice(0, maxLength)}
                inputProps={{ inputMode: "numeric", maxLength }}
            />
        </Box>
    );
};

export default PhoneField;
